import React, { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { Autocomplete, FormControlLabel, Switch, TextField } from "@mui/material";
import { staticReportsQueries } from "constants/reports";
import { Routes } from "constants/routes";
import { useFormik } from "formik";
import { useReportMeasuresGroupByOptions } from "hooks";
import { useDealIdSelector, useReportsQueriesSelector } from "store";
import { GraphInterval, ReportsQueries } from "store/types";

import { AutocompleteMultiSelect } from "..";
import { AutocompleteMeasures } from "../Set-table-columns/AutocompleteMeasures";

import "./DealGraphConfContent.styles.scss";

export type DataFormation = Partial<
  Pick<
    ReportsQueries,
    "mainParameter" | "secondaryParameter" | "interval" | "groupByGraph" | "cumulative" | "columnType"
  >
>;

const intervalOptions = ["Monthly", "Quarterly", "Yearly"];
const noneOption = { text: "None", key: "None", value: "none" };
const optionsToValue: Record<string, GraphInterval> = {
  Monthly: "month",
  Quarterly: "quarter",
  Yearly: "year"
};

type ColumnType = ReportsQueries["columnType"];
const columnTypeOptions: { label: string; value: ColumnType }[] = [
  { label: "Stacked", value: "stacked" },
  { label: "Side by side", value: "side-by-side" }
];

interface GroupByContentProps {
  onChangeDataFormation?: (dataFormation: DataFormation) => void;
}

export const DealGraphConfContent: React.FC<GroupByContentProps> = ({ onChangeDataFormation }) => {
  const { mainParameter, secondaryParameter, interval, cumulative, groupByGraph, columnType } =
    useReportsQueriesSelector();
  const groupByOptions = useReportMeasuresGroupByOptions({ includeDateGroups: false, includeMeasureGroups: false });
  const isDealContext = useDealIdSelector();

  const { pathname } = useLocation();

  const { setFieldValue, values } = useFormik({
    initialValues: { mainParameter, secondaryParameter, interval, cumulative, groupByGraph, columnType },
    onSubmit: () => {},
    enableReinitialize: true
  });

  useEffect(() => {
    onChangeDataFormation?.(values);
  }, [values]);

  const rawFirst = values.groupByGraph?.[0];
  const firstGroupKey = rawFirst != null && rawFirst !== "" && rawFirst !== "none" ? String(rawFirst) : null;
  const selectedGroupOption = firstGroupKey
    ? (groupByOptions.find((o) => String(o.value) === firstGroupKey) ?? null)
    : null;

  const handleGroupByChange = (value: string | null) => {
    setFieldValue("groupByGraph", value ? [value] : null);
    setFieldValue("mainParameter", values.mainParameter?.slice(0, 1) ?? staticReportsQueries.mainParameter);
  };

  return (
    <div className="deal-graph-conf">
      <div className="deal-graph-conf__title">Deal Measures Configuration</div>
      {!isDealContext && (
        <div className="row">
          <div className="input-wrap">
            <AutocompleteMultiSelect
              defaultValue={selectedGroupOption ?? noneOption}
              label="Group by"
              options={groupByOptions}
              onChange={(value) => handleGroupByChange(value === noneOption.value ? null : String(value))}
              stateAfterClear={noneOption}
            />
          </div>
        </div>
      )}
      {selectedGroupOption && !isDealContext && (
        <div className="row">
          <div className="input-wrap">
            <Autocomplete
              options={columnTypeOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={columnTypeOptions.find((o) => o.value === values.columnType) ?? columnTypeOptions[0]}
              onChange={(_, option) => setFieldValue("columnType", option?.value ?? "stacked")}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Column type" />}
            />
          </div>
        </div>
      )}
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMeasures
            label="Right Parameter"
            multiple={!selectedGroupOption}
            queries={{
              measures: selectedGroupOption ? values.mainParameter?.slice(0, 1) : values.mainParameter
            }}
            setQueryValue={() => {}}
            onClear={() => setFieldValue("mainParameter", staticReportsQueries.mainParameter)}
            setValues={(value) => setFieldValue("mainParameter", selectedGroupOption ? value.slice(0, 1) : value)}
            showGroups={["measures", "calculatedFields"]}
          />
        </div>
      </div>
      <div className="row">
        <div className="input-wrap">
          <AutocompleteMeasures
            label="Left Parameter"
            multiple={false}
            queries={{
              measures: values.secondaryParameter ?? []
            }}
            setValues={(value) => setFieldValue("secondaryParameter", value.length ? value : null)}
            onClear={() => setFieldValue("secondaryParameter", null)}
            setQueryValue={() => {}}
            showGroups={["measures", "calculatedFields"]}
          />
        </div>
      </div>
      <div className="row">
        <div className="input-wrap">
          <Autocomplete
            options={intervalOptions}
            value={values.interval && Object.entries(optionsToValue).find((entry) => entry[1] === values.interval)?.[0]}
            onChange={(e, value) => setFieldValue("interval", value && optionsToValue[value])}
            renderInput={(params) => <TextField {...params} label="Interval" />}
          />
        </div>
      </div>

      {/*TODO: Check weather to show this component by other than page path*/}
      {matchPath(pathname, Routes.timeline) && (
        <div className="row">
          <div className="input-wrap">
            <FormControlLabel
              control={
                <Switch
                  sx={{
                    color: "#173c4c",
                    "& .MuiSwitch-switchBase": {
                      "&:hover": {
                        background: "none"
                      },
                      padding: "12px"
                    },
                    "& .MuiSwitch-switchBase.MuiSwitch-colorPrimary": {
                      color: "#bac7ce",
                      boxShadow: "none"
                    },
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#26a6ae",
                      "&:hover": {
                        background: "none"
                      }
                    },
                    "& .MuiSwitch-track": {
                      color: "#bac7ce",
                      height: "20px",
                      borderRadius: "9px"
                    }
                  }}
                  checked={values.cumulative}
                  onChange={(e, value) => setFieldValue("cumulative", value)}
                />
              }
              labelPlacement="end"
              label="Show Cumulative Data"
              slotProps={{
                typography: {
                  marginTop: "5px"
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
